import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import { Banner } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { StartupCohort, type StartupCohortDetails } from '@/services/billing/startup-coupons';
import type { BillingCoupon } from '@/services/billing';
import { useCouponBanner } from '../hooks/use-coupon-banner';
import { BILLING_TIME_ZONE } from '@/services/billing/time-zone';

/**
 * What the program discount gives the cohort. The program states a price rather than a discount:
 * Ordway lists both cohorts at the same price and takes the difference off with the coupon, so the
 * discount states nothing the account can act on.
 */
function startupCohortMessage(details: StartupCohortDetails): string {
  const price = formatCentsToUsd(details.monthlyPriceCents, 0);

  switch (details.cohort) {
    case StartupCohort.Startup:
      return `With Startup, your base plan is discounted to ${price} per month.`;
    case StartupCohort.Elevate:
      return `On Elevate, your base plan is discounted to ${price} per month.`;
    default: {
      const exhaustive: never = details.cohort;
      return String(exhaustive);
    }
  }
}

/**
 * The banner title for the cohort. The first year is still in the program; the second year has
 * graduated, so Elevate reads as an alumni.
 */
function startupCohortTitle(details: StartupCohortDetails): string {
  switch (details.cohort) {
    case StartupCohort.Startup:
      return "You're in the Startup Program!";
    case StartupCohort.Elevate:
      return "You're a Startup Program alumni!";
    default: {
      const exhaustive: never = details.cohort;
      return String(exhaustive);
    }
  }
}

/** Formats coupon-ends unix seconds as e.g. "Jan 05, 2026", in the billing time zone. */
function formatCouponEndDate(couponEndsAt: number): string {
  return formatDateTime(couponEndsAt * 1000, 'medium', { day: '2-digit', timeZone: BILLING_TIME_ZONE });
}

/** The trailing " until {date}." (or plain ".") suffix shared by the discount messages. */
function endsSuffix(couponEndsAt: number | undefined): string {
  if (couponEndsAt == null) {
    return '.';
  }
  return ` until ${formatCouponEndDate(couponEndsAt)}.`;
}

/** The coupon's display name, collapsing the retention "off 3mo" coupon to a friendly label. */
function couponDisplayName(coupon: BillingCoupon | undefined): string {
  const name = coupon?.name ?? '';
  if (name.startsWith('Retention') && name.includes('off 3mo')) {
    return '50% off for 3 months';
  }
  return name;
}

/**
 * Why the plan is discounted. A Startup-program account reads the price of its cohort; every other
 * account reads the coupon that discounts its plan.
 *
 * Renders nothing when the account has no coupon, or when the subscription is not active, so the
 * page spaces only the banners that appear.
 */
export function CouponAppliedAlert() {
  const banner = useCouponBanner();
  if (banner == null) {
    return null;
  }

  const { coupon, planName, couponEndsAt, startupCohort, isPromoCode } = banner;

  if (startupCohort != null) {
    return (
      <Banner
        variant="success"
        icon="feature-launch"
        data-test-coupon-banner="startup"
        title={startupCohortTitle(startupCohort)}
        description={startupCohortMessage(startupCohort)}
      />
    );
  }

  if (coupon == null) {
    return null;
  }

  const suffix = endsSuffix(couponEndsAt);

  if (isPromoCode) {
    return (
      <Banner
        variant="success"
        icon={null}
        data-test-coupon-banner="promo"
        title="You're receiving a discount!"
        description={
          <>
            {couponDisplayName(coupon)} gives you {coupon.percent_off}% off{suffix}
          </>
        }
      />
    );
  }

  return (
    <Banner
      variant="success"
      icon={null}
      data-test-coupon-banner="coupon"
      title="You're receiving a discount!"
      description={
        <>
          With {planName}, you're receiving {couponDisplayName(coupon)}
          {suffix}
        </>
      }
    />
  );
}
